import posthog from "src/consent/posthog"
import ApiHandler from "src/api/ApiHandler"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import StateConstants from "src/states/StateContstants"
import { applyUserEntitlements } from "src/utils/LocalDevAccess"

export const SUBSCRIPTION_PRICE = "€5"
export const SUBSCRIPTION_PRICE_LABEL = `${SUBSCRIPTION_PRICE}/month`

export const PENDING_SUBSCRIBE_KEY = "pendingSubscribeReturnPath"
export const RETURN_PARAM = "subscription"
export const RETURN_VALUE = "return"

export type SubscriptionReturnStatus = "activating" | "activated" | "delayed" | null

export interface SubscriptionState {
    status: string
    currentPeriodEnd: number | null
    cancelAtPeriodEnd: boolean
}

export function readSubscription(userInfo: any): SubscriptionState | null {
    if (!userInfo?.subscription_status) return null
    return {
        status: userInfo.subscription_status,
        currentPeriodEnd: userInfo.subscription_current_period_end ?? null,
        cancelAtPeriodEnd: !!userInfo.subscription_cancel_at_period_end,
    }
}

export function applyUserInfo(stateHandler: EventAdaptingStateHandler, userInfo: any) {
    applyUserEntitlements(
        stateHandler,
        userInfo?.features || [],
        userInfo?.at || "basic",
        userInfo?.free_detailed_valuation_credits_remaining ?? 0,
        userInfo?.free_unlocked_property_ids ?? [],
        userInfo?.permissions ?? [],
        userInfo?.role ?? null
    )
    stateHandler.set(StateConstants.SUBSCRIPTION, readSubscription(userInfo))
    stateHandler.set(StateConstants.USER_EMAIL, userInfo?.e ?? null)
}

export function isPaidAccountType(accountType: string | null | undefined): boolean {
    return !!accountType && !["anonymous", "basic"].includes(accountType)
}

export function currentReturnPath(): string {
    const params = new URLSearchParams(window.location.search)
    params.delete(RETURN_PARAM)
    const query = params.toString()
    return window.location.pathname + (query ? `?${query}` : "")
}

export function isSubscriptionReturn(): boolean {
    return new URLSearchParams(window.location.search).get(RETURN_PARAM) === RETURN_VALUE
}

export function clearSubscriptionReturnParam() {
    window.history.replaceState(window.history.state, "", currentReturnPath() + window.location.hash)
}

export async function startSubscriptionCheckout(
    apiHandler: ApiHandler,
    stateHandler: EventAdaptingStateHandler,
    source: string,
    returnPath: string = currentReturnPath(),
) {
    posthog?.capture("subscription_checkout_started", { source, is_logged_in: !!stateHandler.get(StateConstants.IS_LOGGED_IN) })

    if (!stateHandler.get(StateConstants.IS_LOGGED_IN)) {
        localStorage.setItem(PENDING_SUBSCRIBE_KEY, returnPath)
        stateHandler.set(StateConstants.SHOW_PLAN_MODAL, false)
        stateHandler.set(StateConstants.LOGIN_MODAL_MODE, "signup")
        stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
        return
    }

    const response = await apiHandler.createSubscriptionCheckout(returnPath)
    if (response?.status === "ready" && response.url) {
        window.location.assign(response.url)
        return
    }
    if (response?.status === "already_subscribed") {
        const userInfo = await apiHandler.refreshUserInfo()
        if (userInfo) applyUserInfo(stateHandler, userInfo)
        stateHandler.set(StateConstants.SHOW_PLAN_MODAL, false)
        return
    }
    throw new Error("Could not start the subscription checkout")
}

export async function resumePendingSubscription(apiHandler: ApiHandler, stateHandler: EventAdaptingStateHandler): Promise<boolean> {
    const returnPath = localStorage.getItem(PENDING_SUBSCRIBE_KEY)
    if (!returnPath || !stateHandler.get(StateConstants.IS_LOGGED_IN)) return false
    localStorage.removeItem(PENDING_SUBSCRIBE_KEY)
    if (stateHandler.get(StateConstants.IS_PREMIUM)) return false
    await startSubscriptionCheckout(apiHandler, stateHandler, "resume_after_sign_in", returnPath)
    return true
}

export async function awaitSubscriptionActivation(
    apiHandler: ApiHandler,
    stateHandler: EventAdaptingStateHandler,
    attempts: number = 10,
    intervalMs: number = 1000,
): Promise<boolean> {
    stateHandler.set(StateConstants.SUBSCRIPTION_RETURN_STATUS, "activating")
    for (let attempt = 0; attempt < attempts; attempt++) {
        try {
            const userInfo = await apiHandler.refreshUserInfo()
            if (userInfo && isPaidAccountType(userInfo.at)) {
                applyUserInfo(stateHandler, userInfo)
                stateHandler.set(StateConstants.SUBSCRIPTION_RETURN_STATUS, "activated")
                posthog?.capture("subscription_activated", { attempts: attempt + 1 })
                return true
            }
        } catch (error) {
            console.error("Failed to read the subscription after checkout", error)
        }
        if (attempt < attempts - 1) {
            await new Promise(resolve => setTimeout(resolve, intervalMs))
        }
    }
    stateHandler.set(StateConstants.SUBSCRIPTION_RETURN_STATUS, "delayed")
    return false
}

export async function openBillingPortal(apiHandler: ApiHandler, returnPath: string = currentReturnPath()) {
    posthog?.capture("billing_portal_opened", {})
    const response = await apiHandler.createBillingPortalSession(returnPath)
    if (response?.url) {
        window.location.assign(response.url)
        return
    }
    throw new Error("Could not open the billing portal")
}
